import { useCallback } from 'react';
import { clone } from 'lodash-es';
import { v4 as uuid } from 'uuid';

import type { ContextOfUse, DateRange, FilterType } from '@hotjar/global-filters';
import type { PlanComparisonReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import type { AdditionalTrackingData, FilterChainSeries } from '@hotjar/dashboard-quantz';
import { getSeriesLabel } from '@hotjar/dashboard-quantz';
import { getColorByIndex } from '@hotjar/charts';
import { OneFilterWrapper } from './OneFilterWrapper';
import { AddStepSection } from '../../../TrendsPage/AddStepSection';
import { Steps } from '../../../TrendsPage/Steps';

const MAX_NB_OF_CHAINS = 5;
const MIN_NB_OF_CHAINS = 1;

interface MultiFilterChainProps {
  filterChainSeriesList: FilterChainSeries[];
  dateRange: DateRange;
  onFilterChainSeriesUpdate: (
    newFilterChainSeriesList: FilterChainSeries[],
    newDateRange?: DateRange,
  ) => void;
  onUpsellRequest: (
    featureName: PlanComparisonReferrer,
    additionalTrackingData?: AdditionalTrackingData,
  ) => void;
  trackClick: (eventName: string, additionalTrackingData: AdditionalTrackingData) => void;
  hideHeaderLabel?: boolean;
  maxNbOfChains?: number;
  contextOfUse: ContextOfUse;
}

export const MultiFilterChain = ({
  filterChainSeriesList,
  dateRange,
  hideHeaderLabel,
  maxNbOfChains = MAX_NB_OF_CHAINS,
  contextOfUse,
  onFilterChainSeriesUpdate,
  onUpsellRequest,
  trackClick,
}: MultiFilterChainProps): JSX.Element => {
  const updateOneChain = useCallback(
    (key: string, filtersInUiFormat: FilterType[], newDateRange?: DateRange) => {
      const cloned = clone(filterChainSeriesList);
      const index = cloned.findIndex((item) => item.key === key);
      if (index !== null) {
        cloned[index] = {
          ...cloned[index],
          filtersInUiFormat,
          label: getSeriesLabel(filtersInUiFormat),
        };
        onFilterChainSeriesUpdate(cloned, newDateRange);
      }
    },
    [filterChainSeriesList, onFilterChainSeriesUpdate],
  );

  const addOneChain = useCallback(() => {
    if (filterChainSeriesList.length >= maxNbOfChains) {
      return;
    }
    const filtersInUiFormat: FilterType[] = [];
    const newChain: FilterChainSeries = {
      key: uuid(),
      filtersInUiFormat,
      color: getColorByIndex(filterChainSeriesList.length),
      label: getSeriesLabel(filtersInUiFormat),
    };
    // If there was an autoselected info on the date, we remove it
    const newDateRange: DateRange = {
      ...dateRange,
      autoSelectedConstraint: undefined,
    };
    onFilterChainSeriesUpdate([...filterChainSeriesList, newChain], newDateRange);
  }, [dateRange, filterChainSeriesList, onFilterChainSeriesUpdate, maxNbOfChains]);

  const removeOneChain = useCallback(
    (key: string) => {
      if (filterChainSeriesList.length <= MIN_NB_OF_CHAINS) {
        return;
      }
      const filtered = filterChainSeriesList.filter((item) => item.key !== key);
      // We need to update the color and the label of the remaining series
      const filteredWithUpdatedColor = filtered.map((item, index) => ({
        ...item,
        label: getSeriesLabel(item.filtersInUiFormat),
        color: getColorByIndex(index),
      }));
      // If there was an autoselected info on the date, we remove it
      const newDateRange: DateRange = {
        ...dateRange,
        autoSelectedConstraint: undefined,
      };
      onFilterChainSeriesUpdate(filteredWithUpdatedColor, newDateRange);
    },
    [dateRange, filterChainSeriesList, onFilterChainSeriesUpdate],
  );

  return (
    <Steps>
      {filterChainSeriesList.map((item, index) => {
        const isLastChain = index === filterChainSeriesList.length - 1;
        const isFiltersDropdownVisibleByDefault =
          index !== 0 && isLastChain && !item.filtersInUiFormat.length;

        return (
          <OneFilterWrapper
            key={item.key}
            isFirstChain={index === 0}
            isLastChain={isLastChain && filterChainSeriesList.length >= maxNbOfChains}
            isDeletable={filterChainSeriesList.length > MIN_NB_OF_CHAINS}
            filterChainSeries={item}
            dateRange={dateRange}
            onFiltersChange={(newFiltersInQueryFormat, newDateRange) =>
              updateOneChain(item.key, newFiltersInQueryFormat, newDateRange)
            }
            onChainDelete={() => removeOneChain(item.key)}
            onUpsellRequest={onUpsellRequest}
            trackClick={trackClick}
            isFiltersDropdownVisibleByDefault={isFiltersDropdownVisibleByDefault}
            hideHeaderLabel={hideHeaderLabel}
            contextOfUse={contextOfUse}
          />
        );
      })}
      {filterChainSeriesList.length < maxNbOfChains && (
        <AddStepSection
          addOneChain={addOneChain}
          trackClick={trackClick}
          kind="filter"
          index={filterChainSeriesList.length}
          contextOfUse={contextOfUse}
        />
      )}
    </Steps>
  );
};
